import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCw, Paperclip, MessageCircle } from "lucide-react";
import api, { formatError } from "@/lib/api";

const ST = { sent: "bg-green-600 text-white", failed: "bg-red-600 text-white", skipped: "bg-slate-500 text-white" };

export default function EmailStatus() {
  const [days, setDays] = useState(1);
  const [data, setData] = useState(null);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const load = () => api.get(`/admin/emails?days=${days}`).then(({ data: d }) => setData(d)).catch(() => setData({ emails: [], sent: 0, failed: 0 }));
  useEffect(() => { setData(null); load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = (data?.emails || []).filter((e) => !onlyFailed || e.status === "failed");
  return (
    <div className="space-y-3" data-testid="admin-emails">
      <div className="flex flex-wrap items-center gap-2">
        {[[1, "Today"], [7, "Last 7 days"]].map(([d, l]) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`admin-emails-days-${d}`}
            className={`px-3 py-1.5 rounded-full border-2 text-sm font-semibold ${days === d ? "bg-yellow-300 text-slate-900 border-yellow-300" : "border-slate-600"}`}>{l}</button>
        ))}
        <label className="flex items-center gap-1.5 text-sm text-slate-300 ml-2">
          <input type="checkbox" checked={onlyFailed} onChange={(e) => setOnlyFailed(e.target.checked)} data-testid="admin-emails-only-failed" /> Only failed
        </label>
        {data && <span className="ml-auto text-sm" data-testid="admin-emails-summary"><b className="text-green-400">{data.sent} sent</b> · <b className="text-red-400">{data.failed} failed</b></span>}
      </div>
      {!data ? <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>
        : !rows.length ? <p className="text-slate-400 text-center py-6" data-testid="admin-emails-empty">No emails for this period.</p>
        : rows.map((e) => <Row key={e.id} e={e} onDone={load} />)}
    </div>
  );
}

function Row({ e, onDone }) {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { await api.post(`/admin/emails/${e.id}/resend`); toast.success(`Email resent to ${e.to}`); onDone(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); } finally { setBusy(false); }
  };
  return (
    <div className="bg-slate-800 rounded-xl border-2 border-slate-700 px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm" data-testid={`admin-email-${e.id}`}>
      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${ST[e.status]}`} data-testid={`admin-email-status-${e.id}`}>{e.status}</span>
      <span className="text-xs text-slate-400 w-32">{new Date(e.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
      <span className="text-xs font-semibold text-yellow-200 w-40">{e.kind}</span>
      <span className="font-semibold">{e.to}</span>
      <span className="text-slate-400 flex-1 min-w-[120px] truncate" title={e.subject}>{e.has_attachment && <Paperclip size={12} className="inline mr-1" />}{e.subject}</span>
      {e.whatsapp && (
        <a href={`https://api.whatsapp.com/send?phone=${e.whatsapp.replace(/\D/g, "")}&text=${encodeURIComponent(`${e.subject}\n\n${e.wa_text}`)}`} target="_blank" rel="noreferrer"
          data-testid={`admin-email-whatsapp-${e.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#25D366] text-white px-3 py-1 text-xs font-semibold">
          <MessageCircle size={12} /> Send on WhatsApp
        </a>
      )}
      {e.status === "failed" && (e.resent_at
        ? <span className="text-xs text-green-300" data-testid={`admin-email-resent-${e.id}`}>Resent</span>
        : <button onClick={resend} disabled={busy} data-testid={`admin-email-resend-${e.id}`}
            className="inline-flex items-center gap-1 rounded-full border-2 border-blue-400 text-blue-200 px-3 py-1 text-xs font-semibold hover:bg-blue-500/20">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />} Resend
          </button>)}
      {e.status === "failed" && e.error && <p className="w-full text-xs text-red-300 truncate" title={e.error}>{e.error}</p>}
    </div>
  );
}
